import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { formatBytes, formatDateTime, formatNumber } from '../format';
import type { LocationSummary, Outcome, RecordingQuery, RecordingsResponse, RecordingsSummary } from '../types';
import { OutcomeTag } from '../components/OutcomeTag';
import { StatTile } from '../components/StatTile';
import { Dialog } from '../components/Dialog';
import { FilterPanel } from '../components/FilterPanel';

interface Props {
  locations: LocationSummary[];
  /** Edit level on Files: delete and reclaim space. */
  canEdit?: boolean;
}

const DEFAULT_QUERY: RecordingQuery = {
  locationId: '',
  q: '',
  length: 'all',
  sort: 'date',
  dir: 'desc',
  page: 1,
  pageSize: 50,
};

const SORT_LABELS: Record<RecordingQuery['sort'], string> = {
  date: 'Call date',
  length: 'Length',
  size: 'File size',
  subaccount: 'Sub-account',
  contact: 'Contact',
};

/** m:ss, or h:mm:ss for the long ones — how a player shows length. */
function clock(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

function hours(seconds: number): string {
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  return `${(seconds / 3600).toFixed(seconds >= 36_000 ? 0 : 1)} h`;
}

type Pending =
  | { kind: 'short' }
  | { kind: 'selected'; ids: string[]; bytes: number }
  | null;

/**
 * The Files tab: every call recording stored on the server, what each one
 * costs in disk, and ways to get rid of the ones not worth keeping.
 */
export function Files({ locations, canEdit = true }: Props) {
  const [summary, setSummary] = useState<RecordingsSummary | null>(null);
  const [query, setQuery] = useState<RecordingQuery>(DEFAULT_QUERY);
  const [search, setSearch] = useState('');
  const [data, setData] = useState<RecordingsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [playing, setPlaying] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [working, setWorking] = useState(false);
  const [reload, setReload] = useState(0);

  const compacting = Boolean(summary?.compacting);

  const refreshSummary = useCallback(() => {
    api.recordingsSummary().then(setSummary).catch(() => undefined);
  }, []);

  useEffect(refreshSummary, [refreshSummary, reload]);

  // While a compaction runs, watch it; the list comes back when it finishes.
  useEffect(() => {
    if (!compacting) return;
    const timer = setInterval(refreshSummary, 3000);
    return () => clearInterval(timer);
  }, [compacting, refreshSummary]);

  // Typing in search waits for a pause before asking the server.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery((current) => (current.q === search ? current : { ...current, q: search, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (compacting) return;
    let cancelled = false;
    setLoading(true);
    api
      .recordings(query)
      .then((response) => {
        if (cancelled) return;
        setData(response);
        setError(null);
      })
      .catch((cause) => {
        if (!cancelled) setError(cause instanceof ApiError ? cause.message : String(cause));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [query, compacting, reload]);

  // A selection only means something for what is on screen.
  useEffect(() => setSelected(new Set()), [query]);

  function update(patch: Partial<RecordingQuery>) {
    setQuery((current) => ({ ...current, page: 1, ...patch }));
  }

  function sortBy(sort: RecordingQuery['sort']) {
    setQuery((current) => ({
      ...current,
      page: 1,
      sort,
      // A new column starts at its most useful end: longest, largest, newest; A–Z for names.
      dir: current.sort === sort ? (current.dir === 'asc' ? 'desc' : 'asc') : sort === 'subaccount' || sort === 'contact' ? 'asc' : 'desc',
    }));
  }

  function SortHeader({ column, label, num }: { column: RecordingQuery['sort']; label: string; num?: boolean }) {
    const active = query.sort === column;
    return (
      <th className={num ? 'num' : undefined} aria-sort={active ? (query.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <button type="button" className={`sort-button${active ? ' active' : ''}`} onClick={() => sortBy(column)}>
          {label}
          <span aria-hidden="true">{active ? (query.dir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}</span>
        </button>
      </th>
    );
  }

  async function confirmDelete() {
    if (!pending) return;
    setWorking(true);
    try {
      const result = await api.deleteRecordings(
        pending.kind === 'short' ? { shorterThanSeconds: summary?.minSeconds ?? 60 } : { callIds: pending.ids },
      );
      setNotice(
        `Deleted ${formatNumber(result.deleted)} recording${result.deleted === 1 ? '' : 's'} (${formatBytes(result.bytes)}).` +
          (result.reclaimedNow ? ' The space is free on disk now.' : ' Use “Reclaim disk space” to give the space back to the disk.'),
      );
      setPending(null);
      setSelected(new Set());
      setReload((value) => value + 1);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
      setPending(null);
    } finally {
      setWorking(false);
    }
  }

  async function compact() {
    setWorking(true);
    try {
      const result = await api.compactRecordings();
      if (!result.started && result.reason) setError(result.reason);
      refreshSummary();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setWorking(false);
    }
  }

  const rows = data?.recordings ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / query.pageSize));
  const allOnPage = rows.length > 0 && rows.every((row) => selected.has(row.callId));
  const selectedBytes = rows.filter((row) => selected.has(row.callId)).reduce((sum, row) => sum + row.bytes, 0);
  const minSeconds = summary?.minSeconds ?? 60;
  const short = summary?.short;
  const disk = summary?.disk;
  const lastCompaction = summary?.compaction;

  return (
    <div className="stack">
      {notice ? (
        <div className="banner">
          <span>{notice}</span>
          <button type="button" className="btn btn-sm" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      {error ? (
        <div className="banner error">
          <span>{error}</span>
          <button type="button" className="btn btn-sm" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="grid grid-kpi">
        <StatTile
          label="Stored recordings"
          value={summary?.archive ? formatNumber(summary.archive.stored) : '—'}
          footnote={summary?.archive ? `${formatBytes(summary.archive.bytes)} of audio` : undefined}
        />
        <StatTile
          label="Recordings file"
          value={summary ? formatBytes(summary.files.recordingsBytes) : '—'}
          footnote={
            summary
              ? `Database ${formatBytes(summary.files.databaseBytes)} · logs ${formatBytes(summary.files.logsBytes)}`
              : undefined
          }
        />
        <StatTile
          label="Disk free"
          value={disk ? formatBytes(disk.freeBytes) : '—'}
          footnote={disk ? `of ${formatBytes(disk.totalBytes)} (${Math.round((1 - disk.freeBytes / disk.totalBytes) * 100)}% used)` : undefined}
        />
        <StatTile
          label={`Under ${clock(minSeconds)}`}
          value={short ? formatNumber(short.count) : '—'}
          footnote={short ? `${formatBytes(short.bytes)} you can delete` : undefined}
        />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Storage</h2>
        </div>
        <p className="card-sub">
          Calls shorter than {clock(minSeconds)} are no longer saved; they still play from HighLevel while
          HighLevel keeps them. Set <code>RECORDING_MIN_SECONDS</code> to change the cut-off.
        </p>
        <div className="row">
          <button
            type="button"
            className="btn btn-danger"
            disabled={!canEdit || compacting || !short?.count || working}
            onClick={() => setPending({ kind: 'short' })}
          >
            Delete recordings under {clock(minSeconds)}
            {short?.count ? ` (${formatNumber(short.count)})` : ''}
          </button>
          {compacting ? (
            <span className="small muted">Reclaiming disk space… the list returns when it finishes.</span>
          ) : summary?.reclaimableBytes ? (
            <button type="button" className="btn" disabled={!canEdit || working} onClick={compact}>
              Reclaim {formatBytes(summary.reclaimableBytes)} of disk space
            </button>
          ) : (
            <span className="small muted">
              {summary?.incrementalVacuum
                ? 'Deleting frees disk space straight away.'
                : 'Nothing to reclaim.'}
            </span>
          )}
          {lastCompaction && !lastCompaction.running ? (
            <span className="small muted">
              Last reclaim {formatDateTime(lastCompaction.finishedAt)}:{' '}
              {lastCompaction.error
                ? `failed — ${lastCompaction.error}`
                : `${formatBytes(lastCompaction.beforeBytes)} → ${formatBytes(lastCompaction.afterBytes ?? 0)}`}
            </span>
          ) : null}
        </div>
      </div>

      <FilterPanel
        activeCount={(query.locationId ? 1 : 0) + (query.length !== 'all' ? 1 : 0) + (query.q ? 1 : 0)}
        onReset={() => {
          setSearch('');
          setQuery(DEFAULT_QUERY);
        }}
      >
        <div className="field">
          <label htmlFor="files-location">Sub-account</label>
          <select id="files-location" value={query.locationId} onChange={(event) => update({ locationId: event.target.value })}>
            <option value="">All sub-accounts</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Length</label>
          <div className="segmented" role="group" aria-label="Length">
            {(
              [
                ['all', 'Any'],
                ['short', `< ${clock(minSeconds)}`],
                ['1-5', '1–5 min'],
                ['5-15', '5–15 min'],
                ['15+', '15+ min'],
              ] as [RecordingQuery['length'], string][]
            ).map(([value, label]) => (
              <button
                type="button"
                key={value}
                aria-pressed={query.length === value}
                onClick={() => update({ length: value })}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label htmlFor="files-sort">Sort by</label>
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <select
              id="files-sort"
              value={query.sort}
              onChange={(event) => update({ sort: event.target.value as RecordingQuery['sort'] })}
            >
              {Object.entries(SORT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn"
              aria-label={query.dir === 'asc' ? 'Ascending' : 'Descending'}
              title={query.dir === 'asc' ? 'Ascending' : 'Descending'}
              onClick={() => update({ dir: query.dir === 'asc' ? 'desc' : 'asc' })}
            >
              {query.dir === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
        <div className="field field-grow">
          <label htmlFor="files-search">Search</label>
          <input
            id="files-search"
            type="search"
            placeholder="Contact, phone or user…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </FilterPanel>

      <div className={`card${loading ? ' reloading' : ''}`}>
        <div className="card-head">
          <h2>Recordings</h2>
          {selected.size ? (
            <button
              type="button"
              className="btn btn-sm btn-danger"
              disabled={!canEdit}
              onClick={() => setPending({ kind: 'selected', ids: [...selected], bytes: selectedBytes })}
            >
              Delete {formatNumber(selected.size)} selected ({formatBytes(selectedBytes)})
            </button>
          ) : null}
        </div>
        <p className="card-sub">
          {data
            ? `${formatNumber(data.total)} recording${data.total === 1 ? '' : 's'} · ${hours(data.seconds)} of audio · ${formatBytes(data.bytes)}`
            : compacting
              ? 'Unavailable while disk space is reclaimed.'
              : 'Loading…'}
        </p>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 28 }}>
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    checked={allOnPage}
                    onChange={() =>
                      setSelected(allOnPage ? new Set() : new Set(rows.map((row) => row.callId)))
                    }
                  />
                </th>
                <SortHeader column="date" label="Call date" />
                <SortHeader column="subaccount" label="Sub-account" />
                <SortHeader column="contact" label="Contact" />
                <th>User</th>
                <th>Call</th>
                <SortHeader column="length" label="Length" num />
                <SortHeader column="size" label="Size" num />
                <th>Listen</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.callId}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select recording for ${row.contactName ?? row.contactPhone ?? row.callId}`}
                      checked={selected.has(row.callId)}
                      onChange={() =>
                        setSelected((current) => {
                          const next = new Set(current);
                          if (next.has(row.callId)) next.delete(row.callId);
                          else next.add(row.callId);
                          return next;
                        })
                      }
                    />
                  </td>
                  <td className="nowrap">
                    {formatDateTime(row.callAt)}
                    <div className="cell-sub">Saved {formatDateTime(row.storedAt)}</div>
                  </td>
                  <td>{row.locationName ?? row.locationId}</td>
                  <td>
                    <div className="cell-strong">{row.contactName ?? row.contactPhone ?? 'Unknown contact'}</div>
                    {row.contactName && row.contactPhone ? <div className="cell-sub">{row.contactPhone}</div> : null}
                  </td>
                  <td>{row.userName ?? (row.userId ? row.userId : <span className="muted">Automated</span>)}</td>
                  <td className="nowrap">
                    {row.outcome ? <OutcomeTag outcome={row.outcome as Outcome} /> : null}
                    <div className="cell-sub">{row.direction ?? ''}</div>
                  </td>
                  <td className="num">
                    <span className={row.durationSeconds < minSeconds ? 'length-short' : undefined}>
                      {clock(row.durationSeconds)}
                    </span>
                  </td>
                  <td className="num nowrap">{formatBytes(row.bytes)}</td>
                  <td>
                    {playing === row.callId ? (
                      // eslint-disable-next-line jsx-a11y/media-has-caption
                      <audio controls autoPlay preload="none" src={api.recordingUrl(row.callId)} style={{ height: 30, maxWidth: 220 }} />
                    ) : (
                      <button type="button" className="btn btn-sm" onClick={() => setPlaying(row.callId)}>
                        ▶ Play
                      </button>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      disabled={!canEdit}
                      aria-label="Delete recording"
                      title="Delete recording"
                      onClick={() => setPending({ kind: 'selected', ids: [row.callId], bytes: row.bytes })}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {data && rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="empty">
                    No stored recordings match these filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <div className="row">
            <label htmlFor="files-page-size" className="muted">
              Rows
            </label>
            <select
              id="files-page-size"
              value={query.pageSize}
              onChange={(event) => update({ pageSize: Number(event.target.value) })}
            >
              {[25, 50, 100, 200].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          <div className="row">
            <button
              type="button"
              className="btn btn-sm"
              disabled={query.page <= 1}
              onClick={() => setQuery((current) => ({ ...current, page: current.page - 1 }))}
            >
              Previous
            </button>
            <span>
              Page {query.page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-sm"
              disabled={query.page >= totalPages}
              onClick={() => setQuery((current) => ({ ...current, page: current.page + 1 }))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {pending ? (
        <Dialog title="Delete recordings?" onClose={() => !working && setPending(null)}>
          <p>
            {pending.kind === 'short'
              ? `This deletes all ${formatNumber(short?.count ?? 0)} stored recordings shorter than ${clock(minSeconds)} (${formatBytes(short?.bytes ?? 0)}), across every sub-account.`
              : `This deletes ${pending.ids.length === 1 ? 'this recording' : `${formatNumber(pending.ids.length)} recordings`} (${formatBytes(pending.bytes)}).`}
          </p>
          <p className="small muted">
            The call stays in your reports. Its audio may still play from HighLevel while HighLevel keeps it, but
            the copy saved here is gone and will not be downloaded again.
          </p>
          <div className="dialog-actions">
            <button type="button" className="btn" disabled={working} onClick={() => setPending(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-danger-solid" disabled={working} onClick={confirmDelete}>
              {working ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
